<template>
	<Listbox
		as="div"
		:class="listboxClasses"
		:multiple="multiple"
		:disabled="disabled"
		:v-model="modelValue"
		data-testid="dropdown"
		@update:model-value="updateValue"
		@click.stop>
		<div class="relative flex items-center">
			<ListboxButton ref="buttonRef" :class="classes" @click.stop="adjustDropdownPosition()">
             <span
				 v-if="multiple && selectedOptions"
	             style="max-width: 250px"
	             class="block overflow-x-auto whitespace-nowrap">
                <span v-if="badgeContainer" class="block truncate">
                   <slot></slot>
                   <app-badge v-if="selectedOptions.length > 0" theme="outline" class="ml-1">
                      {{ selectedOptions.length }}
                   </app-badge>
                </span>

                <Teleport :to="badgeContainer" :disabled="badgeContainer === undefined">
                   <span
					   v-for="option in selectedOptions"
	                   :key="option"
	                   :class="badgeClass"
	                   data-testid="selection">
                      {{
						   optionsMap.get(option)
							   ? constructLabel(optionsMap.get(option))
							   : 'Removed Value'
					   }}

                      <button
						  v-if="selectedOptions.length > min"
	                      type="button"
	                      class="group relative -mr-1 h-3.5 w-3.5 rounded-sm hover:bg-gray-500/20"
	                      @click="removeItem(option)">
                         <span class="sr-only">Remove</span>

                         <svg
							 viewBox="0 0 14 14"
	                         class="h-3.5 w-3.5 stroke-gray-600/50 group-hover:stroke-gray-600/75">
                            <path d="M4 4l6 6m0-6l-6 6" />
                         </svg>

                         <span class="absolute -inset-1" />
                      </button>
                   </span>
                </Teleport>
             </span>

				<span
					v-else-if="!multiple && modelValue !== undefined && modelValue !== ''"
					:class="[
                   'block truncate',
                   fontSize,
                   !optionsMap.has(modelValue) ? 'text-red-600' : ''
                ]">
                {{
						optionsMap.has(modelValue)
							? constructLabel(optionsMap.get(modelValue))
							: 'Removed Value'
					}}
             </span>

				<span v-else class="block truncate" :class="[fontSize]">
                <slot></slot>
             </span>

				<span
					v-if="!button"
					data-testid="chevron"
					class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
             </span>
			</ListboxButton>

			<Teleport to="body" :disabled="!teleport">
				<transition
					leave-active-class="transition ease-in duration-100"
					leave-from-class="opacity-100"
					leave-to-class="opacity-0">
					<ListboxOptions
						ref="menuRef"
						data-testid="dropdown-menu"
						class="absolute z-50 mt-1 max-h-60 w-full min-w-[150px] overflow-x-hidden overflow-y-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
						:class="{ 'right-0': align == 'right' && !teleport }">

						<div v-if="searchable" class="sticky top-0 z-20 bg-white border-b border-gray-100 p-2">
							<input
								v-model="searchQuery"
								type="text"
								placeholder="Search..."
								class="w-full rounded border-gray-300 px-3 py-1.5 text-sm focus:border-sky-950 focus:ring-1 focus:ring-sky-950 outline-none"
								@click.stop
								@keydown.space.stop
							/>
						</div>

						<ListboxOption
							v-if="!multiple && !required"
							key="default"
							v-slot="{ active }"
							as="template"
							data-testid="dropdown-option"
							value=""
							@click.stop>
							<li
								:class="[
                            active ? 'bg-sky-950 text-white' : 'text-sky-950',
                            size == 'x-small' ? 'py-0' : 'py-2',
                            'relative cursor-default select-none pl-3 pr-9'
                         ]">
                         <span
							 class="font-normal block truncate text-left"
	                         :class="[
                               size == 'small' || size == 'x-small' ? 'text-sm py-1' : ''
                            ]">
                            <div v-if="$slots.empty">
                               <slot name="empty"></slot>
                            </div>

                            <div v-else>
                               <slot></slot>
                            </div>
                         </span>
							</li>
						</ListboxOption>

						<div v-if="groupedItems.length === 0" class="p-4 text-center text-sm text-gray-500">
							No options found.
						</div>

						<template v-for="(group, index) in groupedItems" :key="index">
							<div
								v-if="group.group"
								:class="[
                            size == 'x-small' ? 'py-0' : 'py-2',
                            'px-3 text-xs font-medium bg-gray-100 text-gray-500'
                         ]">
								{{ group.group }}
							</div>

							<ListboxOption
								v-for="option in group.options"
								:key="valueKey == '' ? option : option[valueKey]"
								v-slot="{ active }"
								as="template"
								data-testid="dropdown-option"
								:value="valueKey == '' ? option : option[valueKey]"
								:disabled="
                            !isSelected(valueKey == '' ? option : option[valueKey]) && maxed
                         "
								@click.stop="select(option)">
								<li
									:class="[
                               active ? 'bg-sky-950 text-white' : 'text-sky-950',
                               !isSelected(valueKey == '' ? option : option[valueKey]) &&
                               maxed
                                  ? 'opacity-5'
                                  : '',
                               size == 'x-small' ? 'py-0' : 'py-2',
                               'relative cursor-default select-none pl-3 pr-9'
                            ]">
                            <span
								:class="[
                                  isSelected(valueKey == '' ? option : option[valueKey])
                                     ? 'font-semibold'
                                     : 'font-normal',
                                  size == 'small' || size == 'x-small'
                                     ? 'text-sm py-1'
                                     : '',
                                  'block truncate text-left'
                               ]">
                               <slot
								   name="option"
	                               :option="option"
	                               :selected="
                                     isSelected(
                                        valueKey == '' ? option : option[valueKey]
                                     )
                                  ">
                                  {{ constructLabel(option) }}
                                </slot>
                            </span>

									<span
										v-if="
                                  isSelected(valueKey == '' ? option : option[valueKey])
                               "
										:class="[
                                  active ? 'text-white' : 'text-blue-600',
                                  size == 'small' || size == 'x-small'
                                     ? 'text-sm py-1'
                                     : '',
                                  'absolute inset-y-0 right-0 flex items-center pr-4'
                               ]">
                               <CheckIcon class="h-5 w-5" aria-hidden="true" />
                            </span>
								</li>
							</ListboxOption>
						</template>

						<div v-if="isTruncated" class="border-t border-gray-100 bg-gray-50 px-3 py-3 text-center">
							<p class="text-xs italic text-gray-400">Showing first 40 results.
								<span class="block font-medium text-sky-950 mt-1">Use search to find more...</span>
							</p>
						</div>
					</ListboxOptions>
				</transition>
			</Teleport>
		</div>
	</Listbox>
</template>

<script lang="ts" setup>
	import { computed, ref, nextTick } from 'vue'
	import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/vue'
	import { CheckIcon, ChevronUpDownIcon } from '@heroicons/vue/20/solid'
	import { isArray } from 'lodash'

	const props = withDefaults(
		defineProps<{
			modelValue?: any
			options: Array<any>
			align?: 'left' | 'right'
			size?: 'x-small' | 'small' | 'medium' | 'large'
			theme?: 'primary' | 'outline'
			inputGroup?: 'left' | 'center' | 'right'
			valueKey?: string
			labelKey?: string | string[]
			multiple?: boolean
			min?: number
			max?: number
			badgeContainer?: string
			disabled?: boolean
			button?: boolean
			boundaryElement?: string
			groupBy?: string
			required?: boolean
			teleport?: boolean
			searchable?: boolean // New Prop
		}>(),
		{
			modelValue: undefined,
			align: 'left',
			size: 'medium',
			valueKey: 'value',
			labelKey: 'label',
			multiple: false,
			min: 0,
			max: undefined,
			badgeContainer: undefined,
			disabled: false,
			button: false,
			theme: 'outline',
			boundaryElement: undefined,
			inputGroup: undefined,
			groupBy: undefined,
			required: false,
			teleport: false,
			searchable: false // Default to false so existing usage is unchanged
		}
	)

	const searchQuery = ref('')

	// Optimization: Quick lookup Map
	const optionsMap = computed(() => {
		const map = new Map()
		props.options.forEach((opt) => {
			const key = props.valueKey === '' ? opt : opt[props.valueKey as string]
			map.set(key, opt)
		})
		return map
	})

	const fontSize = computed(() => {
		if (props.size == 'x-small') return 'text-[11px]'
		if (props.size == 'small') return 'text-xs py-1'
		return 'text-sm'
	})

	const classes = computed(() => ({
		'relative w-full cursor-default pr-10 text-left ring-1 ring-inset focus:outline-none sm:text-sm sm:leading-6': true,
		'py-2 pl-4 text-md': props.size === 'large',
		'py-1.5 pl-3 text-sm': props.size == 'medium',
		'py-1 pl-3 text-xs': props.size === 'small',
		'py-[0px] pl-[10px] text-[10px] rounded min-h-[25px]': props.size === 'x-small',
		'rounded-md shadow-sm': props.size != 'x-small',
		'!rounded-e-none': props.inputGroup == 'left',
		'!rounded-none ml-[-1px]': props.inputGroup == 'center',
		'!rounded-s-none ml-[-1px]': props.inputGroup == 'right',
		'opacity-50 cursor-not-allowed': props.disabled,
		'bg-mabey-950 ring-mabey-950 focus:ring-mabey-800 text-white': props.theme == 'primary',
		'bg-white ring-gray-300 focus:ring-2 focus:ring-mabey-950 text-gray-900': props.theme == 'outline'
	}))

	const listboxClasses = computed(() => ({
		'min-w-[150px]': !props.button
	}))

	const selectedOptions = computed<any[]>(() => {
		if (props.multiple && isArray(props.modelValue)) return props.modelValue
		return []
	})

	const maxed = computed(() => {
		if (!props.max || !selectedOptions.value.length) return false
		return selectedOptions.value.length >= props.max
	})

	const isTruncated = computed(() => {
		return !searchQuery.value && props.options.length > 40
	})

	const badgeClass = computed(() => 'inline-flex mr-1 items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10')

	function isSelected(value: any) {
		if (isArray(props.modelValue)) {
			return props.modelValue.includes(value)
		}
		return props.modelValue == value
	}

	function removeItem(option: any) {
		if (isArray(props.modelValue)) {
			const selections = [...props.modelValue]
			const index = selections.indexOf(option)
			if (index > -1) {
				selections.splice(index, 1)
				emit('update:modelValue', selections)
			}
		}
	}

	function constructLabel(option: any) {
		if (!option) return ''
		if (Array.isArray(props.labelKey)) {
			return props.labelKey.map((key) => option[key]).filter(Boolean).join(' - ')
		}
		return props.labelKey === '' ? option : option[props.labelKey as string]
	}

	const filteredOptions = computed(() => {
		const q = searchQuery.value.toLowerCase()
		const items = searchQuery.value
			? props.options.filter(opt => constructLabel(opt).toLowerCase().includes(q))
			: props.options

		return items.slice(0, 40)
	})

	const groupedItems = computed(() => {
		const items = filteredOptions.value
		if (!props.groupBy) return [{ group: null, options: items }]

		const groups: Record<string, any[]> = items.reduce((acc, option) => {
			const group = option[props.groupBy!]
			if (!acc[group]) acc[group] = []
			acc[group].push(option)
			return acc
		}, {})

		return Object.keys(groups).sort().map((group) => ({ group, options: groups[group] }))
	})

	const emit = defineEmits(['update:modelValue', 'selected'])

	function updateValue(value: any) {
		emit('update:modelValue', value)
	}

	function select(val: object) {
		emit('selected', val)
	}

	const buttonRef = ref()
	const menuRef = ref()

	function adjustDropdownPosition() {
		nextTick(() => {
			if (!menuRef.value?.$el || !buttonRef.value?.$el) return
			const menu = menuRef.value.$el
			const button = buttonRef.value.$el
			const menuRect = menu.getBoundingClientRect()
			const buttonRect = button.getBoundingClientRect()

			if (props.teleport) {
				menu.style.position = 'fixed'
				menu.style.zIndex = '9999'
				const spaceBelow = window.innerHeight - buttonRect.bottom
				if (spaceBelow < menuRect.height && buttonRect.top > spaceBelow) {
					menu.style.top = (buttonRect.top - menuRect.height - 4) + 'px'
				} else {
					menu.style.top = (buttonRect.bottom + 4) + 'px'
				}
				menu.style.left = props.align === 'right' ? (buttonRect.right - menuRect.width) + 'px' : buttonRect.left + 'px'
			} else {
				if (window.innerHeight - buttonRect.bottom < menuRect.height) {
					menu.style.top = 'auto'
					menu.style.bottom = '100%'
				} else {
					menu.style.bottom = 'auto'
					menu.style.top = '100%'
				}
			}
		})
	}
</script>